import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurant } from "@/hooks/useRestaurant";
import { toast } from "sonner";
import { getErrorMessage, logError } from "@/lib/error-messages";

export type JobType = 
  | "conference_bill"
  | "customer_receipt"
  | "cash_withdrawal"
  | "cash_supply"
  | "cash_register_close"
  | "order_production"
  | "kitchen"
  | "bar"
  | "drinks"
  | "portions"
  | "cancellation_receipt"
  | "partial_payment"
  | "delivery_receipt";

export type PaperWidth = "58mm" | "80mm";
export type CutType = "full" | "partial";
export type PrinterProtocol = "epson_standard" | "bematech_legacy" | "generic";

export interface Printer {
  id: string;
  restaurant_id: string;
  name: string;
  printer_name: string;
  is_active: boolean;
  is_default: boolean;
  paper_width: PaperWidth;
  chars_per_line: number;
  auto_cut_enabled: boolean;
  cut_type: CutType;
  extra_lines_before_cut: number;
  protocol: PrinterProtocol;
  created_at: string;
  updated_at: string;
}

export interface PrinterJobType {
  id: string;
  printer_id: string;
  job_type: JobType;
  is_enabled: boolean;
  created_at: string;
}

export interface PrinterWithJobTypes extends Printer {
  job_types: PrinterJobType[];
}

export const JOB_TYPE_LABELS: Record<JobType, string> = {
  conference_bill: "Conferência de Conta",
  customer_receipt: "Via do Cliente",
  cash_withdrawal: "Sangria",
  cash_supply: "Suprimento",
  cash_register_close: "Fechamento de Caixa",
  order_production: "Pedidos de Produção",
  kitchen: "Cozinha",
  bar: "Bar",
  drinks: "Drinks",
  portions: "Porções",
  cancellation_receipt: "Comprov. Cancelamento",
  partial_payment: "Pagamento Parcial",
  delivery_receipt: "Via do Entregador",
};

export function usePrinters() {
  const { activeRestaurant } = useRestaurant();
  const [printers, setPrinters] = useState<PrinterWithJobTypes[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchPrinters = useCallback(async () => {
    if (!activeRestaurant) {
      setPrinters([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const { data: printersData, error: printersError } = await supabase
        .from("printers")
        .select("*")
        .eq("restaurant_id", activeRestaurant.id)
        .order("name");

      if (printersError) throw printersError;

      if (!printersData || printersData.length === 0) {
        setPrinters([]);
        setIsLoading(false);
        return;
      }

      const printerIds = printersData.map((p) => p.id);
      const { data: jobTypesData, error: jobTypesError } = await supabase
        .from("printer_job_types")
        .select("*")
        .in("printer_id", printerIds);

      if (jobTypesError) throw jobTypesError;

      const printersWithJobTypes: PrinterWithJobTypes[] = printersData.map((printer) => ({
        ...printer,
        paper_width: (printer.paper_width || "80mm") as PaperWidth,
        cut_type: (printer.cut_type || "full") as CutType,
        protocol: (printer.protocol || "epson_standard") as PrinterProtocol,
        job_types: (jobTypesData || []).filter((jt) => jt.printer_id === printer.id) as PrinterJobType[],
      }));

      setPrinters(printersWithJobTypes);
    } catch (error) {
      logError(error, "usePrinters.fetchPrinters");
      toast.error(getErrorMessage(error, "carregar", "printer"));
    } finally {
      setIsLoading(false);
    }
  }, [activeRestaurant]);

  useEffect(() => {
    fetchPrinters();
  }, [fetchPrinters]);

  const createPrinter = async (
    name: string,
    printerName: string,
    jobTypes: JobType[],
    settings?: {
      paper_width?: PaperWidth;
      chars_per_line?: number;
      auto_cut_enabled?: boolean;
      cut_type?: CutType;
      extra_lines_before_cut?: number;
      protocol?: PrinterProtocol;
    }
  ): Promise<boolean> => {
    if (!activeRestaurant) return false;

    try {
      const { data: printer, error: printerError } = await supabase
        .from("printers")
        .insert({
          restaurant_id: activeRestaurant.id,
          name,
          printer_name: printerName,
          is_default: printers.length === 0,
          paper_width: settings?.paper_width || "80mm",
          chars_per_line: settings?.chars_per_line || (settings?.paper_width === "58mm" ? 32 : 48),
          auto_cut_enabled: settings?.auto_cut_enabled ?? true,
          cut_type: settings?.cut_type || "full",
          extra_lines_before_cut: settings?.extra_lines_before_cut ?? 3,
          protocol: settings?.protocol || "epson_standard",
        })
        .select()
        .single();

      if (printerError) throw printerError;

      if (jobTypes.length > 0) {
        const jobTypeRecords = jobTypes.map((jt) => ({
          printer_id: printer.id,
          job_type: jt,
          is_enabled: true,
        }));

        const { error: jobTypesError } = await supabase
          .from("printer_job_types")
          .insert(jobTypeRecords);

        if (jobTypesError) throw jobTypesError;
      }

      await fetchPrinters();
      toast.success("Impressora cadastrada com sucesso!");
      return true;
    } catch (error) {
      logError(error, "usePrinters.createPrinter");
      toast.error(getErrorMessage(error, "criar", "printer"));
      return false;
    }
  };

  const updatePrinter = async (
    printerId: string,
    name: string,
    printerName: string,
    isActive: boolean,
    jobTypes: JobType[],
    settings?: {
      paper_width?: PaperWidth;
      chars_per_line?: number;
      auto_cut_enabled?: boolean;
      cut_type?: CutType;
      extra_lines_before_cut?: number;
      protocol?: PrinterProtocol;
    }
  ): Promise<boolean> => {
    try {
      const updateData: Record<string, unknown> = {
        name,
        printer_name: printerName,
        is_active: isActive,
      };
      
      if (settings) {
        if (settings.paper_width) updateData.paper_width = settings.paper_width;
        if (settings.chars_per_line) updateData.chars_per_line = settings.chars_per_line;
        if (settings.auto_cut_enabled !== undefined) updateData.auto_cut_enabled = settings.auto_cut_enabled;
        if (settings.cut_type) updateData.cut_type = settings.cut_type;
        if (settings.extra_lines_before_cut !== undefined) updateData.extra_lines_before_cut = settings.extra_lines_before_cut;
        if (settings.protocol) updateData.protocol = settings.protocol;
      }
      
      const { error: printerError } = await supabase
        .from("printers")
        .update(updateData)
        .eq("id", printerId);

      if (printerError) throw printerError;

      // Remove existing job types
      const { error: deleteError } = await supabase
        .from("printer_job_types")
        .delete()
        .eq("printer_id", printerId);

      if (deleteError) throw deleteError;

      // Add new job types
      if (jobTypes.length > 0) {
        const jobTypeRecords = jobTypes.map((jt) => ({
          printer_id: printerId,
          job_type: jt,
          is_enabled: true,
        }));

        const { error: jobTypesError } = await supabase
          .from("printer_job_types")
          .insert(jobTypeRecords);

        if (jobTypesError) throw jobTypesError;
      }

      await fetchPrinters();
      toast.success("Impressora atualizada com sucesso!");
      return true;
    } catch (error) {
      logError(error, "usePrinters.updatePrinter");
      toast.error(getErrorMessage(error, "atualizar", "printer"));
      return false;
    }
  };

  const deletePrinter = async (printerId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from("printers")
        .delete()
        .eq("id", printerId);

      if (error) throw error;

      await fetchPrinters();
      toast.success("Impressora removida com sucesso!");
      return true;
    } catch (error) {
      logError(error, "usePrinters.deletePrinter");
      toast.error(getErrorMessage(error, "excluir", "printer"));
      return false;
    }
  };

  const setDefaultPrinter = async (printerId: string): Promise<boolean> => {
    if (!activeRestaurant) return false;

    try {
      // Remove default from all
      await supabase
        .from("printers")
        .update({ is_default: false })
        .eq("restaurant_id", activeRestaurant.id);

      // Set new default
      const { error } = await supabase
        .from("printers")
        .update({ is_default: true })
        .eq("id", printerId);

      if (error) throw error;

      await fetchPrinters();
      toast.success("Impressora padrão definida!");
      return true;
    } catch (error) {
      logError(error, "usePrinters.setDefaultPrinter");
      toast.error(getErrorMessage(error, "atualizar", "printer"));
      return false;
    }
  };

  return {
    printers,
    isLoading,
    refetch: fetchPrinters,
    createPrinter,
    updatePrinter,
    deletePrinter,
    setDefaultPrinter,
  };
}
